import { createSlice } from '@reduxjs/toolkit';
import SmsCampaignService from '../../services/smsCampaign';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  campaigns: [],
  campaign: null,
  totalCount: 0,
  pagination: null
};

const slice = createSlice({
  name: 'smsCampaign',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET CAMPAIGNS SUCCESS
    getCampaignsSuccess(state, action) {
      state.isLoading = false;
      state.campaigns = action.payload.data || [];
      state.totalCount = action.payload.pagination?.totalItems || 0;
      state.pagination = action.payload.pagination || null;
    },

    // GET CAMPAIGN SUCCESS
    getCampaignSuccess(state, action) {
      state.isLoading = false;
      state.campaign = action.payload.data || null;
    },

    // APPROVE CAMPAIGN SUCCESS
    approveCampaignSuccess(state, action) {
      state.isLoading = false;
      const updatedCampaign = action.payload.data;
      if (updatedCampaign) {
        const index = state.campaigns.findIndex((campaign) => campaign.id === updatedCampaign.id);
        if (index !== -1) {
          state.campaigns[index] = {
            ...state.campaigns[index],
            status: 'approved_scheduled',
            ...updatedCampaign
          };
        }
        if (state.campaign && state.campaign.id === updatedCampaign.id) {
          state.campaign = {
            ...state.campaign,
            status: 'approved_scheduled',
            ...updatedCampaign
          };
        }
      }
    },

    // REJECT CAMPAIGN SUCCESS
    rejectCampaignSuccess(state, action) {
      state.isLoading = false;
      const updatedCampaign = action.payload.data;
      if (updatedCampaign) {
        const index = state.campaigns.findIndex((campaign) => campaign.id === updatedCampaign.id);
        if (index !== -1) {
          state.campaigns[index] = {
            ...state.campaigns[index],
            status: 'rejected',
            ...updatedCampaign
          };
        }
        if (state.campaign && state.campaign.id === updatedCampaign.id) {
          state.campaign = {
            ...state.campaign,
            status: 'rejected',
            ...updatedCampaign
          };
        }
      }
    },

    // MARK CAMPAIGN SENT SUCCESS
    markCampaignSentSuccess(state, action) {
      state.isLoading = false;
      const updatedCampaign = action.payload.data;
      if (updatedCampaign) {
        const index = state.campaigns.findIndex((campaign) => campaign.id === updatedCampaign.id);
        if (index !== -1) {
          state.campaigns[index] = {
            ...state.campaigns[index],
            status: 'sent',
            ...updatedCampaign
          };
        }
        if (state.campaign && state.campaign.id === updatedCampaign.id) {
          state.campaign = {
            ...state.campaign,
            status: 'sent',
            ...updatedCampaign
          };
        }
      }
    },

    // CLEAR CAMPAIGN
    clearCampaign(state) {
      state.campaign = null;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  startLoading,
  hasError,
  getCampaignsSuccess,
  getCampaignSuccess,
  approveCampaignSuccess,
  rejectCampaignSuccess,
  markCampaignSentSuccess,
  clearCampaign
} = slice.actions;

// ----------------------------------------------------------------------

export function getAdminCampaigns(params = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await SmsCampaignService.getAdminCampaigns(params);
      if (response.success) {
        dispatch(slice.actions.getCampaignsSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.errors || 'Failed to fetch campaigns'));
      }
    } catch (error) {
      console.error('Get campaigns error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch campaigns'));
    }
  };
}

export function getCampaign(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await SmsCampaignService.getCampaignById(id);
      if (response.success) {
        dispatch(slice.actions.getCampaignSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.errors || 'Failed to fetch campaign'));
      }
    } catch (error) {
      console.error('Get campaign error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch campaign'));
    }
  };
}

export function approveCampaign(campaignId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await SmsCampaignService.approveCampaign(campaignId);
      if (response.success) {
        dispatch(slice.actions.approveCampaignSuccess(response));
        return { success: true, data: response.data };
      }
      dispatch(slice.actions.hasError(response.errors || 'Failed to approve campaign'));
      return { success: false, errors: response.errors };
    } catch (error) {
      console.error('Approve campaign error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to approve campaign'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function rejectCampaign(campaignId, reason = '') {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await SmsCampaignService.rejectCampaign(campaignId, reason);
      if (response.success) {
        dispatch(slice.actions.rejectCampaignSuccess(response));
        return { success: true, data: response.data };
      }
      dispatch(slice.actions.hasError(response.errors || 'Failed to reject campaign'));
      return { success: false, errors: response.errors };
    } catch (error) {
      console.error('Reject campaign error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to reject campaign'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function markCampaignSent(campaignId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await SmsCampaignService.markCampaignSent(campaignId);
      if (response.success) {
        dispatch(slice.actions.markCampaignSentSuccess(response));
        return { success: true, data: response.data };
      }
      dispatch(slice.actions.hasError(response.errors || 'Failed to mark campaign as sent'));
      return { success: false, errors: response.errors };
    } catch (error) {
      console.error('Mark campaign sent error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to mark campaign as sent'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}
